import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/axios";
import { FaPlus, FaTrash } from "react-icons/fa";
import Select from "react-select";

const branchOptions = [
  { value: "Rekonic UAE", label: "Rekonic UAE" },
  { value: "Rekonic KSA", label: "Rekonic KSA" }
];

const initialFormData = {
  branch: "",
  relationship_staff: "",
  supplier_name: "",
  phone: "",
  email: "",
  address: "",
  opening_balance: "",
  trn_number: "",
  contact_persons: [{ name: "", number: "", email: "" }]
};

const SupplierForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState(initialFormData);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingData, setLoadingData] = useState(isEditMode);

  // Fetch employees for dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get('/employees/');
        const options = response.data.map(emp => ({
          value: `${emp.first_name} ${emp.middle_name || ''} ${emp.last_name || ''}`.replace(/\s+/g, ' ').trim(),
          label: `${emp.first_name} ${emp.middle_name || ''} ${emp.last_name || ''}`.replace(/\s+/g, ' ').trim()
        }));
        setEmployeeOptions(options);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // If edit mode, pre-fill with existing supplier data
  useEffect(() => {
    if (!isEditMode) return;
    const fetchSupplier = async () => {
      try {
        const response = await api.get(`/suppliers/${id}`);
        const s = response.data;
        setFormData({
          branch: s.branch || "",
          relationship_staff: s.relationship_staff || "",
          supplier_name: s.supplier_name || "",
          phone: s.phone || "",
          email: s.email || "",
          address: s.address || "",
          opening_balance: s.opening_balance !== undefined ? String(s.opening_balance) : "",
          trn_number: s.trn_number || "",
          contact_persons:
            s.contact_persons && s.contact_persons.length > 0
              ? s.contact_persons
              : [{ name: "", number: "", email: "" }]
        });
      } catch (err) {
        console.error("Error loading supplier for edit:", err);
        alert("Failed to load supplier data.");
      } finally {
        setLoadingData(false);
      }
    };
    fetchSupplier();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected ? selected.value : ""
    }));
  };

  const handleContactChange = (index, e) => {
    const { name, value } = e.target;
    const updatedContacts = [...formData.contact_persons];
    updatedContacts[index][name] = value;
    setFormData((prev) => ({ ...prev, contact_persons: updatedContacts }));
  };

  const addContactPerson = () => {
    setFormData((prev) => ({
      ...prev,
      contact_persons: [...prev.contact_persons, { name: "", number: "", email: "" }]
    }));
  };

  const removeContactPerson = (index) => {
    const updatedContacts = formData.contact_persons.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, contact_persons: updatedContacts }));
  };

  const resetForm = () => setFormData(initialFormData);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.branch || !formData.relationship_staff || !formData.supplier_name || !formData.phone || !formData.email) {
      alert("⚠️ Please fill in all required fields.");
      return;
    }

    const payload = {
      ...formData,
      opening_balance: parseFloat(formData.opening_balance) || 0,
      contact_persons: formData.contact_persons
        .filter(cp => cp.name || cp.number || cp.email)
        .map(cp => ({ name: cp.name || "", number: cp.number || "", email: cp.email || "" }))
    };

    try {
      if (isEditMode) {
        await api.put(`/suppliers/${id}`, payload);
        alert("✅ Supplier updated successfully!");
        navigate(`/dashboard/suppliers/${id}`);
      } else {
        await api.post("/suppliers/register", payload);
        alert("✅ Supplier registered successfully!");
        resetForm();
      }
    } catch (error) {
      console.error("❌ Error saving supplier:", error?.response?.data || error);
      alert(`❌ Failed to save supplier:\n${error?.response?.data?.detail || error.message}`);
    }
  };

  if (loadingData) return <div className="container mt-4"><p>Loading supplier data...</p></div>;

  return (
    <form onSubmit={handleSubmit} className="p-4 border rounded shadow bg-light">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">{isEditMode ? "✏️ Edit Supplier" : "Register Supplier"}</h2>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => isEditMode ? navigate(`/dashboard/suppliers/${id}`) : navigate("/dashboard")}
        >
          {isEditMode ? "← Back to Details" : "Back to Dashboard"}
        </button>
      </div>

      <div className="mb-3">
        <label className="form-label">Branch *</label>
        <Select
          options={branchOptions}
          value={branchOptions.find((opt) => opt.value === formData.branch) || null}
          onChange={(sel) => handleSelectChange(sel, "branch")}
          placeholder="Select Branch"
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Relationship Staff *</label>
        <Select
          options={employeeOptions}
          value={employeeOptions.find((opt) => opt.value === formData.relationship_staff) || null}
          onChange={(sel) => handleSelectChange(sel, "relationship_staff")}
          placeholder="Select Relationship Staff"
        />
      </div>

      {["supplier_name", "phone", "email"].map((field) => (
        <div className="mb-3" key={field}>
          <label className="form-label text-capitalize">{field.replace("_", " ")} *</label>
          <input
            type={field === "email" ? "email" : "text"}
            name={field}
            value={formData[field]}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>
      ))}

      <div className="mb-3">
        <label className="form-label">Address</label>
        <textarea name="address" value={formData.address} onChange={handleChange} className="form-control"></textarea>
      </div>

      {/* Contact Persons */}
      <div className="mb-3 p-3 border rounded bg-white">
        <label className="form-label fw-bold">Contact Persons</label>
        {formData.contact_persons.map((person, index) => (
          <div key={index} className="row g-2 mb-2 align-items-center">
            <div className="col-md-3">
              <input type="text" name="name" placeholder="Full Name" value={person.name} onChange={(e) => handleContactChange(index, e)} className="form-control" />
            </div>
            <div className="col-md-3">
              <input type="text" name="number" placeholder="Phone Number" value={person.number} onChange={(e) => handleContactChange(index, e)} className="form-control" />
            </div>
            <div className="col-md-4">
              <input type="email" name="email" placeholder="Email Address" value={person.email} onChange={(e) => handleContactChange(index, e)} className="form-control" />
            </div>
            <div className="col-md-2 text-center">
              {formData.contact_persons.length > 1 && (
                <button type="button" className="btn btn-outline-danger" onClick={() => removeContactPerson(index)}>
                  <FaTrash />
                </button>
              )}
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-outline-success mt-2" onClick={addContactPerson}>
          <FaPlus className="me-1" /> Add Contact Person
        </button>
      </div>

      <div className="mb-3">
        <label className="form-label">Opening Balance</label>
        <input type="number" step="0.01" name="opening_balance" value={formData.opening_balance} onChange={handleChange} className="form-control" />
      </div>

      <div className="mb-3">
        <label className="form-label">TRN Number</label>
        <input type="text" name="trn_number" value={formData.trn_number} onChange={handleChange} className="form-control" />
      </div>

      <button type="submit" className="btn btn-primary w-100">
        {isEditMode ? "Update Supplier" : "Register Supplier"}
      </button>
    </form>
  );
};

export default SupplierForm;
